<template>
  <div class="gcp-dash">
    <header class="dash-head">
      <h1>APIs &amp; Services</h1>

      <div class="head-controls">
        <!-- เลือก project: แสดง label (เช่น "api key 1") value = projectId -->
        <select
          v-model="selectedProjectId"
          class="project-select"
          :disabled="loading || quotaLoading"
          @change="onProjectChange"
        >
          <option v-for="p in projects" :key="p.projectId" :value="p.projectId">
            {{ p.label }}
          </option>
        </select>

        <!-- สถานะ auth -->
        <button v-if="!accessToken" class="signin-btn" @click="signIn">
          Sign in with Google
        </button>
        <button v-else class="signout-btn" @click="signOut">
          Sign out
        </button>
      </div>
    </header>

    <!-- Tabs -->
    <nav class="tab-bar">
      <button
        :class="{ active: activeTab === 'dashboard' }"
        @click="switchTab('dashboard')"
      >Dashboard</button>
      <button
        :class="{ active: activeTab === 'library' }"
        @click="switchTab('library')"
      >Library &amp; Limits</button>
    </nav>

    <p v-if="error" class="error-box">{{ error }}</p>
    <p v-if="!accessToken && !error" class="hint">
      กด "Sign in with Google" เพื่อดูข้อมูล — sign in ครั้งเดียวด้วย account หลัก
      (account ที่ได้รับ role Monitoring Metrics Viewer บนทุก project)
    </p>

    <!-- ============================ TAB: DASHBOARD ============================ -->
    <div v-show="activeTab === 'dashboard'">
      <!-- แถบเลือกช่วงเวลา: quick range หรือ รอบเดือน (billing month) -->
      <div class="time-controls">
        <div class="range-picker">
          <button
            v-for="r in ranges"
            :key="r.label"
            :class="{ active: !selectedMonth && r.hours === selectedHours }"
            @click="changeRange(r.hours)"
          >{{ r.label }}</button>
        </div>

        <!-- รอบเดือนจ่ายเงิน: เลือกเดือน → ช่วงเวลา = วันที่ 1 ถึงสิ้นเดือน (หรือปัจจุบัน) -->
        <div class="month-picker">
          <label>รอบเดือน (billing):</label>
          <select v-model="selectedMonth" :disabled="loading" @change="onMonthChange">
            <option value="">— เลือกเดือน —</option>
            <option v-for="m in billingMonths" :key="m.value" :value="m.value">
              {{ m.label }}
            </option>
          </select>
        </div>
      </div>

      <p v-if="loading" class="loading">กำลังโหลดข้อมูล…</p>

      <!-- แถบสรุปรวม: stat cards -->
      <section v-if="!loading && summaryRows.length" class="overview">
        <div class="stat-card c-cyan">
          <span class="stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="#17b8ce" stroke-width="2.2" stroke-linecap="round">
              <path d="M4 7h13M4 12h9M4 17h13" />
            </svg>
          </span>
          <span class="stat-label">Total requests</span>
          <span class="stat-value">{{ totals.requests.toLocaleString() }}</span>
        </div>

        <div class="stat-card c-peach">
          <span class="stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="#f0876a" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3a9 9 0 1 0 9 9h-9V3z" />
              <path d="M15.5 2.8a9 9 0 0 1 5.7 5.7H15.5V2.8z" />
            </svg>
          </span>
          <span class="stat-label">Errors</span>
          <span class="stat-value">{{ totals.errorPct }}%</span>
        </div>

        <div class="stat-card c-indigo">
          <span class="stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="#4c6ef5" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3.2 2" />
            </svg>
          </span>
          <span class="stat-label">Median latency (avg)</span>
          <span class="stat-value">{{ totals.p50 }} <small>ms</small></span>
        </div>

        <div class="stat-card c-green">
          <span class="stat-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="#5cb87f" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="4" y="4" width="7" height="7" rx="1.5" />
              <rect x="13" y="4" width="7" height="7" rx="1.5" />
              <rect x="4" y="13" width="7" height="7" rx="1.5" />
              <rect x="13" y="13" width="7" height="7" rx="1.5" />
            </svg>
          </span>
          <span class="stat-label">Services</span>
          <span class="stat-value">{{ summaryRows.length }}</span>
        </div>
      </section>

      <!-- Charts -->
      <section v-if="!loading && summaryRows.length" class="chart-grid">
        <div class="card chart-card wide">
          <h2>Traffic — requests/sec ตามช่วงเวลา (top {{ MAX_LINE_SERIES }} services)</h2>
          <div class="chart-box tall"><canvas ref="trafficChart"></canvas></div>
        </div>

        <div class="card chart-card">
          <h2>สัดส่วน requests ต่อ service</h2>
          <div class="chart-box"><canvas ref="donutChart"></canvas></div>
        </div>

        <div class="card chart-card">
          <h2>Latency — median vs p95 (ms)</h2>
          <div class="chart-box"><canvas ref="latencyChart"></canvas></div>
        </div>
      </section>

      <!-- ตารางรายบริการ -->
      <section v-if="accessToken" class="card table-card">
        <h2>
          สรุปรายบริการ — {{ currentProject.label }} ({{ currentProject.projectId }})
          <span v-if="selectedMonth" class="sub"> · รอบเดือน {{ selectedMonthLabel }}</span>
        </h2>
        <p v-if="!loading && !summaryRows.length && !error" class="empty">
          ไม่พบข้อมูลในช่วงเวลาที่เลือก
        </p>
        <!-- ห่อตารางด้วย .table-wrap เพื่อให้เลื่อนแนวนอนได้บนจอแคบ -->
        <div v-if="summaryRows.length" class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th class="num">Requests</th>
                <th class="num">Errors (%)</th>
                <th class="num">Latency, median (ms)</th>
                <th class="num">Latency, 95% (ms)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in summaryRows" :key="row.service">
                <td>{{ row.service }}</td>
                <td class="num">{{ row.requests.toLocaleString() }}</td>
                <td class="num">{{ row.errorPct }}</td>
                <td class="num">{{ row.p50 }}</td>
                <td class="num">{{ row.p95 }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <!-- ============================ TAB: LIBRARY & LIMITS ============================ -->
    <div v-show="activeTab === 'library'">
      <section v-if="accessToken" class="card table-card">
        <div class="lib-head">
          <h2>Quota &amp; Limits — {{ currentProject.label }} ({{ currentProject.projectId }})</h2>
          <div class="lib-controls">
            <select v-model="quotaServiceFilter" class="project-select">
              <option value="">ทุก library</option>
              <option v-for="s in quotaServices" :key="s" :value="s">{{ s }}</option>
            </select>
            <label class="ft-toggle">
              <input type="checkbox" v-model="freeTierOnly" />
              เฉพาะที่มี free tier
            </label>
            <button class="signin-btn" :disabled="quotaLoading" @click="loadQuotas(true)">
              Refresh
            </button>
          </div>
        </div>

        <p v-if="quotaLoading" class="loading">กำลังโหลด quota…</p>
        <p v-if="quotaError" class="error-box">{{ quotaError }}</p>
        <p v-if="!quotaLoading && !quotaError && !quotaRows.length" class="empty">
          ไม่พบข้อมูล quota — quota metrics จะมีค่าเฉพาะ API ที่มีการเรียกใช้งานจริงในช่วงที่ผ่านมา
        </p>

        <!-- ห่อตารางด้วย .table-wrap เพื่อให้เลื่อนแนวนอนได้บนจอแคบ -->
        <div v-if="filteredQuotaRows.length" class="table-wrap">
          <table class="quota-table">
            <thead>
              <tr>
                <th>Library</th>
                <th>Quota</th>
                <th>Limit name</th>
                <th class="num">Usage</th>
                <th class="num">Limit</th>
                <th class="bar-col">% used</th>
                <th class="num">Free tier (จำนวน)</th>
                <th class="bar-col">Free tier (%)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredQuotaRows" :key="row.key">
                <td>{{ row.service }}</td>
                <td class="mono">{{ row.quotaMetric }}</td>
                <td class="mono dim">{{ row.limitName }}</td>
                <td class="num">{{ row.usageDisplay }}</td>
                <td class="num">{{ row.limitDisplay }}</td>
                <td class="bar-col">
                  <div v-if="row.pct != null" class="pct-wrap">
                    <div class="pct-bar">
                      <div
                        class="pct-fill"
                        :class="pctClass(row.pct)"
                        :style="{ width: Math.min(row.pct, 100) + '%' }"
                      ></div>
                    </div>
                    <span class="pct-text" :class="pctClass(row.pct)">{{ row.pct.toFixed(1) }}%</span>
                  </div>
                  <span v-else class="dim">—</span>
                </td>

                <!-- ★ คอลัมน์ใหม่: จำนวนจริง (ยอดสะสม / free tier) เป็นตัวเลขล้วน -->
                <td class="num">
                  <span v-if="row.freeTier">
                    {{ row.freeTier.monthToDate.toLocaleString() }} / {{ row.freeTier.freeUnits.toLocaleString() }}
                    <br />
                    <span class="dim ft-unit">{{ row.freeTier.unit }}</span>
                  </span>
                  <span v-else class="dim">—</span>
                </td>

                <td class="bar-col">
                  <div
                    v-if="row.freeTier"
                    class="pct-wrap"
                    :title="`${row.freeTier.label} · ใช้จริงตั้งแต่ต้นเดือน ${row.freeTier.monthToDate.toLocaleString()} / ${row.freeTier.freeUnits.toLocaleString()} ${row.freeTier.unit} · ถ้าใช้ต่อระดับนี้จนจบเดือนจะอยู่ที่ประมาณ ${row.freeTier.projected.toLocaleString()} ${row.freeTier.unit}`"
                  >
                    <div class="pct-bar">
                      <div
                        class="pct-fill"
                        :class="pctClass(row.freeTier.pct)"
                        :style="{ width: Math.min(row.freeTier.pct, 100) + '%' }"
                      ></div>
                    </div>
                    <span class="pct-text" :class="pctClass(row.freeTier.pct)">
                      {{ row.freeTier.pct.toFixed(0) }}%
                    </span>
                  </div>
                  <span v-else class="dim">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>


import Chart from 'chart.js'; // v2: default export, ไม่ต้อง register อะไร

const CLIENT_ID = '534930403367-tet6vuj87po12bttc29qv2sp2f4h5ect.apps.googleusercontent.com';

const PROJECTS = [
  { label: 'api key 1', projectId: 'micro-flight-477715-u4' },
  { label: 'api key 2', projectId: 'modular-ground-477803-b0' },
  { label: 'api key 3', projectId: 'perfect-lantern-483706-t9' },
  { label: 'api key 4', projectId: 'project-6e358353-5f05-4c04-b9b' },
  { label: 'api key 5', projectId: 'involuted-bird-489011-k2' },
  { label: 'api key 6', projectId: 'custom-graph-489011-j5' },
  { label: 'api key 7', projectId: 'master-choir-503105-p3' },
  { label: 'api key 8', projectId: 'project-cf33e39d-0706-4155-a98' },
];

const SCOPE = 'https://www.googleapis.com/auth/monitoring.read';

// refresh token ล่วงหน้าก่อนหมดอายุกี่วินาที
const REFRESH_MARGIN_SEC = 300;

// key สำหรับเก็บ token ข้าม reload (sessionStorage = อยู่จนปิดแท็บ, ปลอดภัยกว่า localStorage)
const TOKEN_STORAGE_KEY = 'gcp_dash_token';

// วันตัดรอบบิลของเดือน (Google Cloud billing ตัดรอบตามปฏิทิน = วันที่ 1)
const BILLING_CYCLE_DAY = 1;

// โทนสีตาม Google Cloud Console (dark)
const PALETTE = ['#8ab4f8', '#81c995', '#fdd663', '#f28b82', '#c58af9', '#78d9ec', '#ff8bcb', '#aecbfa'];
const GRID_COLOR = '#3c4043';
const TEXT_COLOR = '#9aa0a6';

const MAX_LINE_SERIES = 5; // จำนวน service สูงสุดในกราฟเส้น
const MAX_DONUT_SLICES = 6; // ที่เหลือรวมเป็น "others"
const MAX_BAR_SERVICES = 8;

// ค่า int64 max = quota ไม่จำกัด
const UNLIMITED = 9223372036854775807;

// breakpoint สำหรับปรับ option ของ chart บนจอแคบ
const MOBILE_BP = 640;

const SERVICE_NAME_MAP = {
  'directions-backend': 'Directions API',
  'geocoding-backend': 'Geocoding API',
  'maps-backend': 'Maps JavaScript API',
  'places-backend': 'Places API',
  'mapsbilling-pa': 'Maps Platform Billing',
  monitoring: 'Cloud Monitoring API',
  recommender: 'Recommender API',
  bigqueryreservation: 'BigQuery Reservation API',
  bigquery: 'BigQuery API',
  logging: 'Cloud Logging API',
  compute: 'Compute Engine API',
  storage: 'Cloud Storage API',
  'cloudresourcemanager-pa': 'Resource Manager API',
};

// ⚠️ หมายเหตุสำคัญ: ตัวเลข freeUnits ด้านล่างนี้ตอนนี้ "อิงจาก quota rate limit ที่เห็นใน
// ตาราง Limit" ซึ่งเป็นคนละก้อนกับ free tier ของบิลลิ่งจริง (quota limit = เพดานเรียก API
// ต่อนาที/วัน, free tier = โควตาฟรีต่อเดือนตามราคาที่ Google ประกาศ) ใช้เป็นค่าเริ่มต้น
// ชั่วคราวเท่านั้น — ก่อนใช้งานจริงต้องเช็ค/แก้ให้ตรงกับหน้าราคาทางการของ Google Maps
// Platform (https://mapsplatform.google.com/pricing/) ก่อนเสมอ
const FREE_TIER_RULES = [
  {
    match: (service, q) => service === 'places-backend'
      && /autocomplete/i.test(q) && !/details/i.test(q),
    label: 'Autocomplete (without Places Details) — อิงจาก quota limit ปัจจุบัน โปรดเช็คราคาจริง',
    freeUnits: 5000,
    unit: 'ครั้ง/เดือน',
  },
  {
    match: (service, q) => service === 'places-backend'
      && /place.?details/i.test(q) && !/atmosphere/i.test(q) && !/contact/i.test(q),
    label: 'Places Details (Basic) — อิงจาก quota limit ปัจจุบัน โปรดเช็คราคาจริง',
    freeUnits: 5000,
    unit: 'ครั้ง/เดือน',
  },
  {
    match: (service, q) => service === 'places-backend' && /atmosphere/i.test(q),
    label: 'Atmosphere Data — อิงจาก quota limit ปัจจุบัน โปรดเช็คราคาจริง',
    freeUnits: 1000,
    unit: 'ครั้ง/เดือน',
  },
  {
    match: (service, q) => service === 'places-backend' && /contact/i.test(q),
    label: 'Contact Data — อิงจาก quota limit ปัจจุบัน โปรดเช็คราคาจริง',
    freeUnits: 1000,
    unit: 'ครั้ง/เดือน',
  },
  {
    match: (service, q) => service === 'maps-backend' && /dynamic.?street/i.test(q),
    label: 'Dynamic Street View — อิงจาก quota limit ปัจจุบัน โปรดเช็คราคาจริง',
    freeUnits: 5000,
    unit: 'ครั้ง/เดือน',
  },
  {
    match: (service) => service === 'geocoding-backend',
    label: 'Geocoding API — อิงจาก quota limit ปัจจุบัน โปรดเช็คราคาจริง',
    freeUnits: 10000,
    unit: 'ครั้ง/เดือน',
  },
  {
    match: (service, q) => service === 'maps-backend' && /dynamic.?maps/i.test(q),
    label: 'Dynamic Maps — อิงจาก quota limit ปัจจุบัน โปรดเช็คราคาจริง',
    freeUnits: 10000,
    unit: 'map loads/เดือน',
  },
  {
    match: (service) => service === 'directions-backend',
    label: 'Directions/Routes API — อิงจาก quota limit ปัจจุบัน โปรดเช็คราคาจริง',
    freeUnits: 10000,
    unit: 'ครั้ง/เดือน',
  },
  // เพิ่ม rule ใหม่ได้ตรงนี้ เช่น:
  // {
  //   match: (service, quotaMetricFull) => service === 'places-backend'
  //     && /autocomplete/i.test(quotaMetricFull),
  //   label: 'Places Autocomplete — free tier โดยประมาณ',
  //   freeUnits: 70000,
  //   unit: 'session/เดือน',
  // },
];

function findFreeTierRule(service, quotaMetricFull) {
  return FREE_TIER_RULES.find((r) => r.match(service, quotaMetricFull)) || null;
}

/** id ดิบ (เช่น directions-backend) → ชื่ออ่านง่าย (เช่น Directions API) */
function friendlyServiceName(id) {
  return SERVICE_NAME_MAP[id] || id;
}

export default {
  name: 'ApiMetricsDashboard',

  data() {
    return {
      loading: false,
      error: null,
      accessToken: null,
      tokenClient: null,
      projects: PROJECTS,
      selectedProjectId: PROJECTS[0].projectId,

      // tabs
      activeTab: 'dashboard', // 'dashboard' | 'library'

      // ช่วงเวลา dashboard
      selectedHours: 24 * 14,
      selectedMonth: '', // '' = ใช้ quick range, 'YYYY-MM' = รอบเดือน
      ranges: [
        { label: '1 hour', hours: 1 },
        { label: '6 hours', hours: 6 },
        { label: '12 hours', hours: 12 },
        { label: '1 day', hours: 24 },
        // { label: '7 days', hours: 24 * 7 },
        // { label: '14 days', hours: 24 * 14 },
        // { label: '30 days', hours: 24 * 30 },
      ],

      summaryRows: [],
      trafficSeries: [], // เก็บ time series ดิบไว้วาดกราฟเส้น
      MAX_LINE_SERIES,

      // library / quota tab
      quotaLoading: false,
      quotaError: null,
      quotaRows: [],
      quotaLoadedFor: null, // projectId ที่โหลด quota ไว้แล้ว (กันโหลดซ้ำ)
      quotaServiceFilter: '',
      freeTierOnly: false, // true = โชว์เฉพาะ quota ที่มี free tier rule จับคู่ได้

      // responsive: true เมื่อจอแคบกว่า MOBILE_BP (ใช้ปรับ legend/แกนของ chart)
      isMobile: typeof window !== 'undefined' && window.innerWidth <= MOBILE_BP,
    };
  },

  computed: {
    currentProject() {
      return this.projects.find((p) => p.projectId === this.selectedProjectId) || this.projects[0];
    },

    baseUrl() {
      return `https://monitoring.googleapis.com/v3/projects/${this.selectedProjectId}/timeSeries`;
    },

    totals() {
      const requests = this.summaryRows.reduce((t, r) => t + r.requests, 0);
      const errors = this.summaryRows.reduce((t, r) => t + (r.errorCount || 0), 0);
      const p50s = this.summaryRows.filter((r) => r.p50 > 0);
      const p50 = p50s.length
        ? Math.round(p50s.reduce((t, r) => t + r.p50, 0) / p50s.length)
        : 0;
      return {
        requests,
        errorPct: requests ? ((errors / requests) * 100).toFixed(2) : '0.00',
        p50,
      };
    },

    /** รายการรอบเดือนย้อนหลัง 12 เดือน (ตัดรอบตาม BILLING_CYCLE_DAY) */
    billingMonths() {
      const opts = [];
      const now = new Date();
      for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const label = d.toLocaleDateString('th-TH', { month: 'long', year: 'numeric' });
        opts.push({ value, label });
      }
      return opts;
    },

    selectedMonthLabel() {
      const m = this.billingMonths.find((x) => x.value === this.selectedMonth);
      return m ? m.label : '';
    },

    /** รายชื่อ library ทั้งหมดใน quota rows (สำหรับ filter dropdown) */
    quotaServices() {
      return [...new Set(this.quotaRows.map((r) => r.service))].sort();
    },

    filteredQuotaRows() {
      let rows = this.quotaRows;
      if (this.quotaServiceFilter) {
        rows = rows.filter((r) => r.service === this.quotaServiceFilter);
      }
      if (this.freeTierOnly) {
        rows = rows.filter((r) => r.freeTier);
      }
      return rows;
    },
  },

  mounted() {
    this.charts = {}; // เก็บ Chart instance (ไม่ต้อง reactive)
    this.refreshTimer = null;
    this.initTokenClient();
    this.tryRestoreSession(); // ★ กู้ session ตอนโหลดหน้า: ใช้ token เดิม/ขอเงียบ ก่อนเด้ง popup

    // responsive: ฟัง resize แบบ debounce — ถ้าข้ามฝั่ง breakpoint ให้วาด chart ใหม่
    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        const nowMobile = window.innerWidth <= MOBILE_BP;
        if (nowMobile !== this.isMobile) {
          this.isMobile = nowMobile;
          if (this.activeTab === 'dashboard' && this.summaryRows.length) {
            this.renderCharts();
          }
        }
      }, 200);
    };
    window.addEventListener('resize', this.onResize);
  },

  beforeDestroy() {
    clearTimeout(this.refreshTimer);
    clearTimeout(this.resizeTimer);
    window.removeEventListener('resize', this.onResize);
    this.destroyCharts();
  },

  methods: {
    /* ---------- OAuth ---------- */

    initTokenClient() {
      if (!window.google || !window.google.accounts) {
        setTimeout(() => this.initTokenClient(), 300);
        return;
      }
      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPE,
        callback: (resp) => {
          if (resp.error) {
            this.error = `OAuth error: ${resp.error}`;
            if (this.tokenReject) this.tokenReject(new Error(resp.error));
          } else {
            this.accessToken = resp.access_token;
            const expiresIn = Number(resp.expires_in) || 3600;
            this.saveToken(resp.access_token, expiresIn); // ★ เก็บ token ไว้ข้าม reload
            // ตั้ง refresh อัตโนมัติก่อน token หมดอายุ
            this.scheduleRefresh(expiresIn);
            if (this.tokenResolve) this.tokenResolve(resp.access_token);
          }
          this.tokenResolve = null;
          this.tokenReject = null;
        },
      });
    },

    requestToken({ silent = false } = {}) {
      return new Promise((resolve, reject) => {
        this.tokenResolve = resolve;
        this.tokenReject = reject;
        this.tokenClient.requestAccessToken(silent ? { prompt: '' } : {});
      });
    },

    /* ---------- Token persistence (ข้าม reload) ---------- */

    saveToken(token, expiresInSec) {
      try {
        const expiresAt = Date.now() + expiresInSec * 1000;
        sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({ token, expiresAt }));
      } catch (e) { /* storage อาจถูกปิด — ข้ามไป */ }
    },

    loadStoredToken() {
      try {
        const raw = sessionStorage.getItem(TOKEN_STORAGE_KEY);
        if (!raw) return null;
        const { token, expiresAt } = JSON.parse(raw);
        // เหลืออายุน้อยกว่า margin ถือว่าใช้ไม่ได้ (ให้ไปขอใหม่แบบเงียบแทน)
        if (!token || expiresAt - Date.now() < REFRESH_MARGIN_SEC * 1000) return null;
        return { token, expiresAt };
      } catch (e) { return null; }
    },

    clearStoredToken() {
      try { sessionStorage.removeItem(TOKEN_STORAGE_KEY); } catch (e) { /* noop */ }
    },

    /** กู้ session ตอนโหลดหน้า: ใช้ token เดิมถ้ายังไม่หมด, ไม่งั้นขอแบบเงียบ */
    async tryRestoreSession() {
      // 1) มี token ใน storage ที่ยังไม่หมดอายุ → ใช้เลย ไม่ต้องยิง network
      const stored = this.loadStoredToken();
      if (stored) {
        this.accessToken = stored.token;
        this.scheduleRefresh(Math.round((stored.expiresAt - Date.now()) / 1000));
        await this.loadAll();
        if (this.activeTab === 'library') this.loadQuotas(true);
        return;
      }
      // 2) ไม่มี token แต่ Google session ยังอยู่ + เคย consent → ขอแบบเงียบ (ไม่เด้ง popup)
      if (!this.tokenClient) { // tokenClient อาจยังไม่พร้อม → รอ
        setTimeout(() => this.tryRestoreSession(), 400);
        return;
      }
      try {
        await this.requestToken({ silent: true });
        await this.loadAll();
        if (this.activeTab === 'library') this.loadQuotas(true);
      } catch (e) {
        // เงียบไว้ ไม่ต้องโชว์ error — รอผู้ใช้กด Sign in เอง
      }
    },

    /** refresh token แบบเงียบ ก่อนหมดอายุ REFRESH_MARGIN_SEC วินาที */
    scheduleRefresh(expiresInSec) {
      clearTimeout(this.refreshTimer);
      const ms = Math.max((expiresInSec - REFRESH_MARGIN_SEC) * 1000, 60 * 1000);
      this.refreshTimer = setTimeout(async () => {
        try {
          await this.requestToken({ silent: true });
        } catch (e) {
          this.accessToken = null;
          this.clearStoredToken();
          this.error = 'Session หมดอายุ กรุณา sign in ใหม่';
        }
      }, ms);
    },

    async signIn() {
      this.error = null;
      try {
        await this.requestToken();
        await this.loadAll();
        if (this.activeTab === 'library') this.loadQuotas(true);
      } catch (e) {
        this.error = e.message;
      }
    },

    signOut() {
      clearTimeout(this.refreshTimer);
      this.clearStoredToken(); // ★ ลบ token ที่เก็บไว้ ไม่ให้ reload แล้วกลับมา login เอง
      this.revokeToken();
      this.summaryRows = [];
      this.trafficSeries = [];
      this.quotaRows = [];
      this.quotaLoadedFor = null;
      this.destroyCharts();
    },

    revokeToken() {
      if (this.accessToken && window.google) {
        window.google.accounts.oauth2.revoke(this.accessToken, () => {});
      }
      this.accessToken = null;
    },

    /* ---------- Events ---------- */

    switchTab(tab) {
      this.activeTab = tab;
      // เข้า tab library ครั้งแรก (หรือเปลี่ยน project มา) → โหลด quota
      if (tab === 'library' && this.accessToken && this.quotaLoadedFor !== this.selectedProjectId) {
        this.loadQuotas();
      }
      // กลับมา dashboard ต้องวาด chart ใหม่ (canvas ถูกซ่อนด้วย v-show จึงยังอยู่ แต่ resize เพี้ยนได้)
      if (tab === 'dashboard' && this.summaryRows.length) {
        this.$nextTick(() => this.renderCharts());
      }
    },

    async onProjectChange() {
      // token เดียวใช้ได้ทุก project (สิทธิ์ตัดสินด้วย IAM ฝั่ง GCP)
      this.error = null;
      this.quotaLoadedFor = null;
      this.quotaServiceFilter = '';
      if (!this.accessToken) return;
      if (this.activeTab === 'library') {
        await this.loadQuotas();
      } else {
        await this.loadAll();
      }
    },

    changeRange(hours) {
      this.selectedHours = hours;
      this.selectedMonth = ''; // quick range ยกเลิกโหมดรอบเดือน
      if (this.accessToken) this.loadAll();
    },

    onMonthChange() {
      if (this.accessToken) this.loadAll();
    },

    /* ---------- Time helpers ---------- */

    /** คืนช่วงเวลา start/end ตามโหมดที่เลือก (quick range หรือรอบเดือน) */
    currentInterval() {
      let start;
      let end;
      if (this.selectedMonth) {
        const [y, m] = this.selectedMonth.split('-').map(Number);
        // รอบเดือน: จากวันตัดรอบของเดือนนั้น → วันตัดรอบของเดือนถัดไป (หรือตอนนี้)
        start = new Date(y, m - 1, BILLING_CYCLE_DAY, 0, 0, 0);
        const cycleEnd = new Date(y, m, BILLING_CYCLE_DAY, 0, 0, 0);
        const now = new Date();
        end = cycleEnd > now ? now : cycleEnd;
      } else {
        end = new Date();
        start = new Date(end.getTime() - this.selectedHours * 3600 * 1000);
      }
      return { start, end };
    },

    timeParams() {
      const { start, end } = this.currentInterval();
      const durSec = Math.max(60, (end.getTime() - start.getTime()) / 1000);
      const alignSec = Math.max(60, Math.round(durSec / 200 / 60) * 60);
      return {
        start: start.toISOString(),
        end: end.toISOString(),
        alignmentPeriod: `${alignSec}s`,
      };
    },

    /* ---------- Data ---------- */

    /**
     * ดึง time series จาก Monitoring API
     * @param {object} q  filter/aligner/reducer/groupBy/interval
     *   - groupBy: array ของ field (default = resource.labels.service)
     *   - interval: {start,end,alignmentPeriod} (default = ช่วงเวลาที่เลือกบน dashboard)
     */
    async fetchTimeSeries(q, retried = false) {
      const {
        filter, aligner, reducer,
        groupBy = ['resource.labels.service'],
        interval = null,
      } = q;
      const { start, end, alignmentPeriod } = interval || this.timeParams();
      const params = new URLSearchParams({
        filter,
        'interval.startTime': start,
        'interval.endTime': end,
        'aggregation.alignmentPeriod': alignmentPeriod,
        'aggregation.perSeriesAligner': aligner,
      });
      if (reducer) {
        params.append('aggregation.crossSeriesReducer', reducer);
        groupBy.forEach((f) => params.append('aggregation.groupByFields', f));
      }

      const res = await fetch(`${this.baseUrl}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${this.accessToken}` },
      });

      if (res.status === 401 && !retried) {
        await this.requestToken({ silent: true });
        return this.fetchTimeSeries(q, true);
      }
      if (res.status === 403) {
        throw new Error(
          `ไม่มีสิทธิ์ดู project "${this.selectedProjectId}" — account นี้ต้องได้ role `
          + 'Monitoring Metrics Viewer บน project นั้น (IAM & Admin → IAM → Grant access)',
        );
      }
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Monitoring API ${res.status}: ${body}`);
      }
      const json = await res.json();
      return json.timeSeries || [];
    },

    async loadAll() {
      this.loading = true;
      this.error = null;
      this.summaryRows = [];
      this.trafficSeries = [];
      this.destroyCharts();
      try {
        const [traffic, errors, latency, p95] = await Promise.all([
          this.fetchTimeSeries({
            filter: 'metric.type="serviceruntime.googleapis.com/api/request_count" AND resource.type="consumed_api"',
            aligner: 'ALIGN_RATE',
            reducer: 'REDUCE_SUM',
          }),
          this.fetchTimeSeries({
            filter: 'metric.type="serviceruntime.googleapis.com/api/request_count" AND resource.type="consumed_api" AND (metric.labels.response_code_class="4xx" OR metric.labels.response_code_class="5xx")',
            aligner: 'ALIGN_RATE',
            reducer: 'REDUCE_SUM',
          }),
          this.fetchTimeSeries({
            filter: 'metric.type="serviceruntime.googleapis.com/api/request_latencies" AND resource.type="consumed_api"',
            aligner: 'ALIGN_PERCENTILE_50',
            reducer: 'REDUCE_MEAN',
          }),
          this.fetchTimeSeries({
            filter: 'metric.type="serviceruntime.googleapis.com/api/request_latencies" AND resource.type="consumed_api"',
            aligner: 'ALIGN_PERCENTILE_95',
            reducer: 'REDUCE_MEAN',
          }),
        ]);

        this.trafficSeries = traffic;
        this.buildSummary({ traffic, errors, latency, p95 });

        // รอ DOM (v-if ของ chart section) แล้วค่อยวาด
        this.$nextTick(() => this.renderCharts());
      } catch (e) {
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },

    buildSummary({ traffic, errors, latency, p95 }) {
      const acc = {};
      const key = (s) =>
        (s.resource && s.resource.labels && s.resource.labels.service) || 'unknown';
      const pointVal = (p) =>
        p.value.doubleValue != null
          ? p.value.doubleValue
          : Number(p.value.int64Value || 0);
      const sumPoints = (s) => (s.points || []).reduce((t, p) => t + pointVal(p), 0);
      const avgPoints = (s) => {
        const pts = s.points || [];
        return pts.length ? sumPoints(s) / pts.length : 0;
      };
      const { alignmentPeriod } = this.timeParams();
      const bucketSec = parseInt(alignmentPeriod, 10);

      traffic.forEach((s) => {
        const k = key(s);
        acc[k] = acc[k] || {};
        acc[k].requests = Math.round(sumPoints(s) * bucketSec);
      });
      errors.forEach((s) => {
        const k = key(s);
        acc[k] = acc[k] || {};
        acc[k].errors = Math.round(sumPoints(s) * bucketSec);
      });
      latency.forEach((s) => {
        const k = key(s);
        acc[k] = acc[k] || {};
        acc[k].p50 = Math.round(avgPoints(s) * 1000);
      });
      p95.forEach((s) => {
        const k = key(s);
        acc[k] = acc[k] || {};
        acc[k].p95 = Math.round(avgPoints(s) * 1000);
      });

      this.summaryRows = Object.keys(acc)
        .map((service) => {
          const r = acc[service];
          const requests = r.requests || 0;
          const errCount = r.errors || 0;
          const serviceId = service.replace('.googleapis.com', '');
          return {
            serviceId, // raw id เช่น 'directions-backend' — ใช้จับคู่กับ trafficSeries
            service: friendlyServiceName(serviceId), // ชื่อโชว์ เช่น 'Directions API'
            requests,
            errorCount: errCount,
            errorPct: requests ? ((errCount / requests) * 100).toFixed(2) : '0',
            p50: r.p50 || 0,
            p95: r.p95 || 0,
          };
        })
        .sort((a, b) => b.requests - a.requests);
    },

    /* ---------- Quota / Library tab ---------- */

    /**
     * โหลด quota limit + usage ต่อ library จาก quota metrics
     * (resource.type="consumer_quota" — ใช้ scope monitoring.read เดิมได้)
     */
    async loadQuotas(force = false) {
      if (!this.accessToken) return;
      if (!force && this.quotaLoadedFor === this.selectedProjectId) return;

      this.quotaLoading = true;
      this.quotaError = null;
      this.quotaRows = [];
      try {
        const now = new Date();
        const iso = (d) => d.toISOString();
        const groupByLimit = [
          'resource.labels.service',
          'metric.labels.quota_metric',
          'metric.labels.limit_name',
        ];
        const groupByUsage = ['resource.labels.service', 'metric.labels.quota_metric'];

        // รอบเดือนสำหรับประมาณการ free tier: ใช้ BILLING_CYCLE_DAY เดียวกับ dashboard
        // เพื่อให้สอดคล้องกับรอบบิลจริง ไม่ใช่ปฏิทินเดือนเสมอไป
        const mtdY = now.getFullYear();
        const mtdM = now.getMonth();
        let cycleStart = new Date(mtdY, mtdM, BILLING_CYCLE_DAY, 0, 0, 0);
        if (cycleStart > now) {
          // ยังไม่ถึงวันตัดรอบเดือนนี้ → รอบปัจจุบันเริ่มจากเดือนก่อน
          cycleStart = new Date(mtdY, mtdM - 1, BILLING_CYCLE_DAY, 0, 0, 0);
        }
        const cycleEnd = new Date(cycleStart);
        cycleEnd.setMonth(cycleEnd.getMonth() + 1);

        const [limits, ratePerMin, ratePerDay, alloc, monthToDate] = await Promise.all([
          // เพดาน quota ปัจจุบัน (gauge, เขียนค่าเป็นระยะ → ดูย้อนหลัง 26 ชม.เอาค่าล่าสุด)
          this.fetchTimeSeries({
            filter: 'metric.type="serviceruntime.googleapis.com/quota/limit" AND resource.type="consumer_quota"',
            aligner: 'ALIGN_NEXT_OLDER',
            reducer: 'REDUCE_MAX',
            groupBy: groupByLimit,
            interval: {
              start: iso(new Date(now.getTime() - 26 * 3600 * 1000)),
              end: iso(now),
              alignmentPeriod: '3600s',
            },
          }),
          // rate quota: usage ต่อนาที (พีคใน 1 ชม.ล่าสุด)
          this.fetchTimeSeries({
            filter: 'metric.type="serviceruntime.googleapis.com/quota/rate/net_usage" AND resource.type="consumer_quota"',
            aligner: 'ALIGN_SUM',
            reducer: 'REDUCE_SUM',
            groupBy: groupByUsage,
            interval: {
              start: iso(new Date(now.getTime() - 3600 * 1000)),
              end: iso(now),
              alignmentPeriod: '60s',
            },
          }),
          // rate quota: ยอดรวม 24 ชม.ล่าสุด (สำหรับ limit แบบ per day + ใช้ประมาณการรายเดือน)
          this.fetchTimeSeries({
            filter: 'metric.type="serviceruntime.googleapis.com/quota/rate/net_usage" AND resource.type="consumer_quota"',
            aligner: 'ALIGN_SUM',
            reducer: 'REDUCE_SUM',
            groupBy: groupByUsage,
            interval: {
              start: iso(new Date(now.getTime() - 24 * 3600 * 1000)),
              end: iso(now),
              alignmentPeriod: '86400s',
            },
          }),
          // allocation quota: ค่าปัจจุบัน
          this.fetchTimeSeries({
            filter: 'metric.type="serviceruntime.googleapis.com/quota/allocation/usage" AND resource.type="consumer_quota"',
            aligner: 'ALIGN_NEXT_OLDER',
            reducer: 'REDUCE_MAX',
            groupBy: groupByUsage,
            interval: {
              start: iso(new Date(now.getTime() - 26 * 3600 * 1000)),
              end: iso(now),
              alignmentPeriod: '3600s',
            },
          }),
          // rate quota: ยอดสะสมจริงตั้งแต่ต้นรอบเดือนถึงวันนี้ (bucket รายวัน)
          // ใช้ทำ free tier run-rate estimate แทนการเดาจากวันเดียว
          this.fetchTimeSeries({
            filter: 'metric.type="serviceruntime.googleapis.com/quota/rate/net_usage" AND resource.type="consumer_quota"',
            aligner: 'ALIGN_SUM',
            reducer: 'REDUCE_SUM',
            groupBy: groupByUsage,
            interval: {
              start: iso(cycleStart),
              end: iso(now),
              alignmentPeriod: '86400s',
            },
          }),
        ]);

        this.quotaRows = this.buildQuotaRows({
          limits,
          ratePerMin,
          ratePerDay,
          alloc,
          monthToDate,
          cycleStart,
          cycleEnd,
          now,
        });
        this.quotaLoadedFor = this.selectedProjectId;
      } catch (e) {
        this.quotaError = e.message;
      } finally {
        this.quotaLoading = false;
      }
    },

    buildQuotaRows({
      limits, ratePerMin, ratePerDay, alloc, monthToDate, cycleStart, cycleEnd, now,
    }) {
      const svc = (s) =>
        ((s.resource && s.resource.labels && s.resource.labels.service) || 'unknown');
      const mlabel = (s, name) =>
        (s.metric && s.metric.labels && s.metric.labels[name]) || '';
      const pointVal = (p) =>
        p.value.doubleValue != null
          ? p.value.doubleValue
          : Number(p.value.int64Value || 0);
      const latest = (s) => {
        const pts = s.points || [];
        return pts.length ? pointVal(pts[0]) : 0; // points เรียงใหม่→เก่า
      };
      const peak = (s) => (s.points || []).reduce((m, p) => Math.max(m, pointVal(p)), 0);
      // ยอดรวมของ series (ใช้กับ monthToDate ที่มีหลาย bucket รายวัน)
      const sum = (s) => (s.points || []).reduce((t, p) => t + pointVal(p), 0);

      // usage maps: key = service|quota_metric
      const uKey = (s) => `${svc(s)}|${mlabel(s, 'quota_metric')}`;
      const perMinMap = {};
      ratePerMin.forEach((s) => { perMinMap[uKey(s)] = peak(s); });
      const perDayMap = {};
      ratePerDay.forEach((s) => { perDayMap[uKey(s)] = peak(s); });
      const mtdMap = {}; // ยอดสะสมจริงตั้งแต่ต้นรอบเดือนถึงวันนี้
      (monthToDate || []).forEach((s) => { mtdMap[uKey(s)] = sum(s); });
      const allocMap = {};
      alloc.forEach((s) => { allocMap[uKey(s)] = latest(s); });

      // run-rate: ยอดสะสมจริง + (อัตราเฉลี่ยต่อ ms ที่ผ่านมา × ms ที่เหลือของรอบเดือน)
      // กัน elapsed สั้นเกินไป (เช่น เพิ่งขึ้นเดือนใหม่) ไม่ให้ตัวคูณพุ่งเกินจริง
      const elapsedMs = cycleStart && now
        ? Math.max(now.getTime() - cycleStart.getTime(), 60 * 60 * 1000)
        : null;
      const totalMs = cycleStart && cycleEnd ? cycleEnd.getTime() - cycleStart.getTime() : null;

      const fmt = (n) => (n >= 10000 ? Math.round(n).toLocaleString() : Number(n.toFixed(2)).toLocaleString());

      const rows = limits.map((s) => {
        const service = svc(s).replace('.googleapis.com', '');
        const quotaMetricFull = mlabel(s, 'quota_metric');
        const limitName = mlabel(s, 'limit_name');
        const limit = latest(s);
        const k = `${svc(s)}|${quotaMetricFull}`;

        // เดาหน่วยของ limit จากชื่อ: per day / per minute / allocation
        const isPerDay = /day|daily|\/d\b/i.test(limitName);
        let usage;
        let usageNote;
        if (k in allocMap && !(k in perMinMap)) {
          usage = allocMap[k];
          usageNote = 'ปัจจุบัน';
        } else if (isPerDay) {
          usage = perDayMap[k] != null ? perDayMap[k] : 0;
          usageNote = '24 ชม.';
        } else {
          usage = perMinMap[k] != null ? perMinMap[k] : 0;
          usageNote = 'พีค/นาที';
        }

        const unlimited = limit < 0 || limit >= UNLIMITED;
        const pct = !unlimited && limit > 0 ? (usage / limit) * 100 : null;

        // ---------- เทียบยอดสะสมจริง (month-to-date) กับ free tier ----------
        // % หลักคำนวณจากยอดจริงล้วนๆ — รู้ตรงๆ ว่าตอนนี้เกินหรือยัง ไม่ต้องเดา
        // ส่วนคาดการณ์ทั้งเดือน (run-rate) เก็บไว้แค่โชว์เสริมใน tooltip เฉยๆ
        let freeTier = null;
        const rule = findFreeTierRule(service, quotaMetricFull);
        if (rule) {
          const mtd = Math.round(mtdMap[k] != null ? mtdMap[k] : 0);
          let projected = mtd;
          if (elapsedMs && totalMs) {
            const remainingMs = Math.max(totalMs - elapsedMs, 0);
            const avgPerMs = mtd / elapsedMs;
            projected = Math.round(mtd + avgPerMs * remainingMs);
          }
          freeTier = {
            label: rule.label,
            freeUnits: rule.freeUnits,
            unit: rule.unit,
            monthToDate: mtd,
            projected, // แค่ข้อมูลเสริม ไม่ใช้คำนวณ pct หลัก
            pct: rule.freeUnits > 0 ? (mtd / rule.freeUnits) * 100 : null,
          };
        }

        return {
          key: `${service}|${quotaMetricFull}|${limitName}`,
          service,
          quotaMetric: quotaMetricFull
            .replace(`${svc(s)}/`, '')
            .replace('.googleapis.com', ''),
          limitName,
          limit,
          limitDisplay: unlimited ? 'ไม่จำกัด' : fmt(limit),
          usage,
          usageDisplay: `${fmt(usage)} (${usageNote})`,
          pct,
          freeTier,
        };
      });

      // เรียงตาม % used มาก → น้อย, ตามด้วยชื่อ service
      return rows.sort((a, b) => {
        const pa = a.pct != null ? a.pct : -1;
        const pb = b.pct != null ? b.pct : -1;
        return pb - pa || a.service.localeCompare(b.service);
      });
    },

    pctClass(pct) {
      if (pct >= 80) return 'p-red';
      if (pct >= 50) return 'p-yellow';
      return 'p-green';
    },

    /* ---------- Charts ---------- */

    destroyCharts() {
      if (!this.charts) return;
      Object.values(this.charts).forEach((c) => c && c.destroy());
      this.charts = {};
    },

    renderCharts() {
      this.destroyCharts();
      this.renderTrafficChart();
      this.renderDonutChart();
      this.renderLatencyChart();
    },

    /** line: requests/sec ตามเวลา แยกตาม service (top N ตามยอด request) */
    renderTrafficChart() {
      const canvas = this.$refs.trafficChart;
      if (!canvas || !this.trafficSeries.length) return;

      const topServices = this.summaryRows
        .slice(0, MAX_LINE_SERIES)
        .map((r) => r.service);

      const shortName = (s) =>
        ((s.resource && s.resource.labels && s.resource.labels.service) || 'unknown')
          .replace('.googleapis.com', '');
      const pointVal = (p) =>
        p.value.doubleValue != null
          ? p.value.doubleValue
          : Number(p.value.int64Value || 0);

      const datasets = this.trafficSeries
        .filter((s) => topServices.includes(shortName(s)))
        .map((s, i) => ({
          label: shortName(s),
          // points จาก API เรียงใหม่→เก่า — กลับด้านให้เวลาเดินซ้าย→ขวา
          data: (s.points || [])
            .slice()
            .reverse()
            .map((p) => ({
              x: new Date(p.interval.endTime).getTime(),
              y: Number(pointVal(p).toFixed(3)),
            })),
          borderColor: PALETTE[i % PALETTE.length],
          backgroundColor: PALETTE[i % PALETTE.length],
          borderWidth: 1.5,
          fill: false, // v2 เติมสีใต้เส้นเป็นค่า default — ปิดไว้
          pointRadius: 0,
          lineTension: 0.25,
        }));

      const { start, end } = this.currentInterval();
      const spanHours = (end - start) / 3600000;
      this.charts.traffic = new Chart(canvas, {
        type: 'line',
        data: { datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          hover: { mode: 'nearest', intersect: false },
          legend: {
            labels: { fontColor: TEXT_COLOR, boxWidth: 12 },
            // จอแคบ: legend หลายรายการกินที่ — ยังโชว์แต่ให้ตัวเล็กลง
            position: this.isMobile ? 'bottom' : 'top',
          },
          tooltips: {
            mode: 'nearest',
            intersect: false,
            callbacks: {
              title: (items, data) => {
                if (!items.length) return '';
                const it = items[0];
                const pt = data.datasets[it.datasetIndex].data[it.index];
                return new Date(pt.x).toLocaleString();
              },
            },
          },
          scales: {
            xAxes: [{
              type: 'linear',
              position: 'bottom',
              ticks: {
                fontColor: TEXT_COLOR,
                maxTicksLimit: this.isMobile ? 4 : 8,
                callback: (v) => {
                  const d = new Date(v);
                  return spanHours <= 24
                    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
                },
              },
              gridLines: { color: GRID_COLOR },
            }],
            yAxes: [{
              ticks: { beginAtZero: true, fontColor: TEXT_COLOR },
              scaleLabel: { display: !this.isMobile, labelString: 'req/s', fontColor: TEXT_COLOR },
              gridLines: { color: GRID_COLOR },
            }],
          },
        },
      });
    },

    /** donut: สัดส่วน requests ต่อ service (top N + others) */
    renderDonutChart() {
      const canvas = this.$refs.donutChart;
      if (!canvas || !this.summaryRows.length) return;

      const top = this.summaryRows.slice(0, MAX_DONUT_SLICES);
      const rest = this.summaryRows.slice(MAX_DONUT_SLICES);
      const labels = top.map((r) => r.service);
      const values = top.map((r) => r.requests);
      if (rest.length) {
        labels.push('others');
        values.push(rest.reduce((t, r) => t + r.requests, 0));
      }

      this.charts.donut = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: PALETTE,
            borderColor: '#202124',
            borderWidth: 2,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutoutPercentage: 62,
          // จอแคบ: legend ด้านขวาบีบ donut จนเล็ก → ย้ายลงล่าง
          legend: {
            position: this.isMobile ? 'bottom' : 'right',
            labels: { fontColor: TEXT_COLOR, boxWidth: 12 },
          },
          tooltips: {
            callbacks: {
              label: (item, data) => {
                const value = data.datasets[0].data[item.index] || 0;
                const label = data.labels[item.index];
                const total = values.reduce((t, v) => t + v, 0) || 1;
                const pct = ((value / total) * 100).toFixed(1);
                return ` ${label}: ${value.toLocaleString()} (${pct}%)`;
              },
            },
          },
        },
      });
    },

    /** bar: latency median vs p95 ต่อ service */
    renderLatencyChart() {
      const canvas = this.$refs.latencyChart;
      if (!canvas || !this.summaryRows.length) return;

      const rows = this.summaryRows
        .filter((r) => r.p50 > 0 || r.p95 > 0)
        .slice(0, MAX_BAR_SERVICES);
      if (!rows.length) return;

      this.charts.latency = new Chart(canvas, {
        type: 'horizontalBar', // v2: แนวนอนเป็น type แยก
        data: {
          labels: rows.map((r) => r.service),
          datasets: [
            {
              label: 'median',
              data: rows.map((r) => r.p50),
              backgroundColor: '#8ab4f8',
            },
            {
              label: 'p95',
              data: rows.map((r) => r.p95),
              backgroundColor: '#fdd663',
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { labels: { fontColor: TEXT_COLOR, boxWidth: 12 } },
          scales: {
            xAxes: [{
              ticks: { beginAtZero: true, fontColor: TEXT_COLOR },
              scaleLabel: { display: !this.isMobile, labelString: 'ms', fontColor: TEXT_COLOR },
              gridLines: { color: GRID_COLOR },
            }],
            yAxes: [{
              ticks: {
                fontColor: TEXT_COLOR,
                // จอแคบ: ตัดชื่อ service ยาว ๆ ไม่ให้กินพื้นที่กราฟ
                callback: (v) => (this.isMobile && String(v).length > 14
                  ? `${String(v).slice(0, 13)}…`
                  : v),
              },
              gridLines: { display: false },
            }],
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.gcp-dash {
  background: #202124;
  color: #e8eaed;
  min-height: 100vh;
  padding: 24px;
  font-family: 'Google Sans', Roboto, 'Noto Sans Thai', sans-serif;
}
.dash-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}
.dash-head h1 { font-size: 20px; font-weight: 400; margin: 0; }
.head-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

/* Tabs */
.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #3c4043;
  margin-bottom: 16px;
  /* จอแคบ: เลื่อนแนวนอนได้ ไม่ตัด tab */
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.tab-bar button {
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: #9aa0a6;
  padding: 10px 18px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}
.tab-bar button.active {
  color: #8ab4f8;
  border-bottom-color: #8ab4f8;
}

/* time controls */
.time-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.range-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.month-picker {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  color: #9aa0a6;
}
.month-picker select {
  background: #2d2e31;
  color: #e8eaed;
  border: 1px solid #3c4043;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  max-width: 100%;
}

.project-select {
  background: #2d2e31;
  color: #e8eaed;
  border: 1px solid #3c4043;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  max-width: 100%;
}
.range-picker button {
  background: transparent;
  border: none;
  color: #e8eaed;
  padding: 6px 12px;
  border-radius: 16px;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
}
.range-picker button.active { background: #3c4043; }
.signin-btn, .signout-btn {
  border: 1px solid #8ab4f8;
  background: transparent;
  color: #8ab4f8;
  padding: 6px 14px;
  border-radius: 16px;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
}
.signin-btn:disabled { opacity: 0.5; cursor: default; }
.signout-btn { border-color: #3c4043; color: #9aa0a6; }

.ft-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #9aa0a6;
  white-space: nowrap;
  cursor: pointer;
}

/* stat cards แบบพาสเทล (ตามภาพตัวอย่าง) */
.overview {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}
.stat-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 26px 16px;
  border-radius: 14px;
  text-align: center;
  min-width: 0; /* กัน overflow เวลาเลขยาว */
}
.stat-card.c-cyan   { background: #e3f7fb; }
.stat-card.c-peach  { background: #fdeee5; }
.stat-card.c-indigo { background: #e6e8fb; }
.stat-card.c-green  { background: #e6f4ec; }
.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(32, 33, 36, 0.12);
  margin-bottom: 6px;
}
.stat-icon svg { width: 26px; height: 26px; }
.stat-card .stat-label {
  color: #5f6368;
  font-size: 16px;
  font-weight: 500;
}
.stat-card .stat-value {
  color: #202124;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
  word-break: break-word;
}
.stat-card .stat-value small {
  font-size: 15px;
  font-weight: 500;
  color: #5f6368;
}

/* chart grid: กราฟเส้นเต็มแถว, donut + bar แบ่งครึ่ง */
.chart-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}
.chart-card.wide { grid-column: 1 / -1; }
.chart-card { min-width: 0; } /* กัน canvas ดัน grid ให้ล้น */
.chart-box { position: relative; height: 260px; }
.chart-box.tall { height: 300px; }

.card {
  background: #202124;
  border: 1px solid #3c4043;
  border-radius: 8px;
  padding: 16px 20px;
}
.card h2 { font-size: 15px; font-weight: 400; margin: 0 0 12px; overflow-wrap: anywhere; }
.card h2 .sub { color: #9aa0a6; font-size: 13px; }

/* ตาราง: ห่อด้วย .table-wrap ให้เลื่อนแนวนอนบนจอแคบ */
.table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
table { width: 100%; border-collapse: collapse; font-size: 14px; }
.table-wrap table { min-width: 560px; }
.table-wrap .quota-table { min-width: 1050px; }
th, td { padding: 10px 12px; border-bottom: 1px solid #3c4043; text-align: left; }
th { color: #9aa0a6; font-weight: 500; }
.num { text-align: right; white-space: nowrap; }
.mono { font-family: 'Roboto Mono', monospace; font-size: 12.5px; overflow-wrap: anywhere; }
.dim { color: #9aa0a6; }
.empty { color: #9aa0a6; font-size: 14px; }
.hint { color: #9aa0a6; font-size: 14px; }
.hint.small { font-size: 12px; margin-top: 10px; line-height: 1.6; }
.hint.small a { color: #8ab4f8; }
.hint.small code {
  background: #2d2e31;
  padding: 1px 5px;
  border-radius: 4px;
  font-family: 'Roboto Mono', monospace;
}
.error-box {
  background: #3c1e1e;
  border: 1px solid #f28b82;
  color: #f28b82;
  padding: 10px 14px;
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-all;
}
.loading { color: #9aa0a6; }

/* Library tab */
.lib-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}
.lib-head h2 { margin: 0; }
.lib-controls { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
.quota-table td { vertical-align: middle; }
.bar-col { width: 170px; }
.pct-wrap { display: flex; align-items: center; gap: 8px; }
.pct-bar {
  flex: 1;
  height: 6px;
  background: #3c4043;
  border-radius: 3px;
  overflow: hidden;
}
.pct-fill { height: 100%; border-radius: 3px; }
.pct-fill.p-green  { background: #81c995; }
.pct-fill.p-yellow { background: #fdd663; }
.pct-fill.p-red    { background: #f28b82; }
.pct-text { font-size: 12px; min-width: 40px; text-align: right; }
.pct-text.p-green  { color: #81c995; }
.pct-text.p-yellow { color: #fdd663; }
.pct-text.p-red    { color: #f28b82; }
.ft-unit { font-size: 11px; }

/* ============================ RESPONSIVE ============================ */

/* แท็บเล็ต / จอกลาง */
@media (max-width: 1024px) {
  .overview { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 900px) {
  .chart-grid { grid-template-columns: 1fr; }
  .chart-box.tall { height: 260px; }
}

/* มือถือ */
@media (max-width: 640px) {
  .gcp-dash { padding: 12px; }

  .dash-head {
    flex-direction: column;
    align-items: stretch;
  }
  .dash-head h1 { font-size: 18px; }
  .head-controls {
    width: 100%;
  }
  .head-controls .project-select {
    flex: 1;
    min-width: 0;
  }

  .time-controls {
    flex-direction: column;
    align-items: stretch;
  }
  .range-picker {
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 4px;
  }
  .month-picker { justify-content: space-between; }
  .month-picker select { flex: 1; min-width: 0; }

  .overview { gap: 10px; }
  .stat-card { padding: 18px 10px; gap: 6px; }
  .stat-icon { width: 44px; height: 44px; }
  .stat-icon svg { width: 22px; height: 22px; }
  .stat-card .stat-label { font-size: 13px; }
  .stat-card .stat-value { font-size: 22px; }

  .card { padding: 12px 14px; }
  .chart-box { height: 220px; }
  .chart-box.tall { height: 220px; }

  th, td { padding: 8px 8px; font-size: 13px; }
  .mono { font-size: 11.5px; }
  .bar-col { width: 130px; }
  .pct-text { min-width: 36px; font-size: 11px; }

  .lib-head { flex-direction: column; align-items: stretch; }
  .lib-controls { width: 100%; }
  .lib-controls .project-select { flex: 1; min-width: 0; }
}

/* จอเล็กมาก */
@media (max-width: 400px) {
  .overview { grid-template-columns: 1fr; }
  .stat-card { padding: 16px 12px; }
}
</style>